import '../color-picker/qds-color-picker.js';
import '../font-picker/qds-font-picker.js';
import '../icon-picker/qds-icon-picker.js';
import type { QdsConfigField, QdsPropertyFieldPluginElement } from '../../types/blocks.js';
import { normalizeTypographyStyle } from '../shared/typography-utils.js';
import { loadBaseCSS } from '../shared/button-utils.js';

/**
 * Escapes a value for safe interpolation into HTML text content or a
 * double-quoted attribute value.
 */
function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function cloneEditorValue<T>(value: T): T {
  if (value === undefined || value === null) {
    return value;
  }

  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * @element qds-property-editor
 * 
 * @attr {string} label - The label for the property editor
 * @attr {boolean} expanded - Whether the property editor is expanded
 * @attr {boolean} readonly - Whether editing controls are disabled
 * 
 * @property {Object} target - The target object being edited
 * @property {Object} metadata - Metadata about the properties being edited
 * @property {Object} context - Optional host context
 * @property {boolean} readonly - Whether editing controls are disabled
 * 
 * @fires change - When a property value changes
 */
export class PropertyEditor extends HTMLElement {
  private _target: Record<string, any> = {};
  private _metadata: Record<string, QdsConfigField> = {};
  private _context: unknown;
  private _label: string = 'Properties';
  private _expanded: boolean = true;
  private _readonly = false;
  private _warnedMissingEditors = new Set<string>();
  private _listenersBound = false;
  
  static get observedAttributes() {
    return ['label', 'expanded', 'readonly'];
  }
  
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._ensureBaseStylesLoaded();
    this.render();
  }
  
  connectedCallback() {
    if (!this.isConnected) return;
    this._addEventListeners();
  }
  
  disconnectedCallback() {
    this._removeEventListeners();
  }
  
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'label':
        this._label = newValue;
        break;
      case 'expanded':
        this._expanded = newValue !== null;
        break;
      case 'readonly':
        this._readonly = newValue !== null;
        break;
    }
    
    this.render();
  }
  
  /**
   * Sets the target object to be edited
   * @param {Object} target - The object to edit
   */
  set target(target: Record<string, any>) {
    this._target = target || {};
    this.render();
  }
  
  /**
   * Gets the current target object
   * @returns {Object} The current target object
   */
  get target(): Record<string, any> {
    return this._target;
  }
  
  /**
   * Sets metadata for the properties
   * @param {Object} metadata - Metadata for the properties
   */
  set metadata(metadata: Record<string, any>) {
    this._metadata = metadata || {};
    this.render();
  }
  
  /**
   * Gets the current metadata
   * @returns {Object} The current metadata
   */
  get metadata(): Record<string, any> {
    return this._metadata;
  }

  /**
   * Sets the host context for field-level plugin editors
   * @param {unknown} context - Host context object
   */
  set context(context: unknown) {
    this._context = context;
    this.render();
  }

  /**
   * Gets the current host context
   * @returns {unknown}
   */
  get context(): unknown {
    return this._context;
  }

  /**
   * Sets readonly mode for the editor
   * @param {boolean} value - Whether the editor is readonly
   */
  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    if (this._readonly) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
    this.render();
  }

  /**
   * Gets the readonly state
   * @returns {boolean}
   */
  get readonly(): boolean {
    return this._readonly;
  }
  
  /**
   * Ensures the base CSS styles are loaded
   * @private
   */
  private _ensureBaseStylesLoaded() {
    loadBaseCSS();
  }
  
  /**
   * Adds event listeners to the component
   * @private
   */
  private _addEventListeners() {
    if (!this.shadowRoot || this._listenersBound) return;

    // Listeners are delegated on the persistent shadowRoot, so bind once and
    // track the bound state to avoid accumulating duplicates across
    // disconnect/reconnect cycles.
    this.shadowRoot.addEventListener('click', this._handleClick);
    this.shadowRoot.addEventListener('change', this._handleChange);
    this.shadowRoot.addEventListener('input', this._handleInput);
    this.shadowRoot.addEventListener('value-change', this._handleValueChange as EventListener);
    this._listenersBound = true;
  }

  private _handleClick = (event: Event): void => {
    const target = event.target as HTMLElement;

    // Handle header clicks for toggling expanded state
    if (target.closest('.property-editor-header') ||
        target.id === 'editor-header' ||
        target.id === 'expander-button' ||
        target.closest('#expander-button')) {
      this._toggleExpanded();
    }
  };

  private _handleChange = (event: Event): void => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
    if (target?.dataset.property) {
      this._updatePropertyValueFromControl(target);
    }
  };

  private _handleInput = (event: Event): void => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
    if (target?.dataset.property && target.type === 'range') {
      this._updatePropertyValueFromControl(target);
    }
  };

  private _handleValueChange = (event: CustomEvent<{ value?: unknown }>): void => {
    const target = event.target as HTMLElement | null;
    const propertyName = target?.dataset.property;
    if (!propertyName || !target?.dataset.pluginEditor) {
      return;
    }

    const detail = event.detail;
    if (!detail || !Object.prototype.hasOwnProperty.call(detail, 'value')) {
      return;
    }

    this._updatePropertyValue(propertyName, detail.value, 'plugin');
  };

  /**
   * Removes event listeners from the component
   * @private
   */
  private _removeEventListeners() {
    if (!this.shadowRoot || !this._listenersBound) return;
    this.shadowRoot.removeEventListener('click', this._handleClick);
    this.shadowRoot.removeEventListener('change', this._handleChange);
    this.shadowRoot.removeEventListener('input', this._handleInput);
    this.shadowRoot.removeEventListener('value-change', this._handleValueChange as EventListener);
    this._listenersBound = false;
  }
  
  /**
   * Toggles the expanded state
   * @private
   */
  private _toggleExpanded() {
    this._expanded = !this._expanded;
    
    // Update attribute
    if (this._expanded) {
      this.setAttribute('expanded', '');
    } else {
      this.removeAttribute('expanded');
    }
    
    this.render();
  }
  
  
  
  /**
   * Updates the property value based on input
   * @param {HTMLInputElement} input - The input element
   * @private
   */
  private _updatePropertyValueFromControl(control: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): void {
    const propertyName = control.dataset.property;
    if (!propertyName) {
      return;
    }

    const rawValue = control instanceof HTMLInputElement && control.type === 'checkbox'
      ? control.checked
      : control.value;

    this._updatePropertyValue(propertyName, rawValue, 'native');
  }

  /**
   * Updates the property value from either a native control or plugin editor.
   * @param {string} propertyName - Property being updated
   * @param {unknown} rawValue - Incoming value to coerce
   * @param {'native' | 'plugin'} source - Update source
   * @private
   */
  private _updatePropertyValue(propertyName: string, rawValue: unknown, source: 'native' | 'plugin'): void {
    if (!this._target) {
      return;
    }

    const metadata = this._getMetadataForProperty(propertyName);
    const nextValue = this._coercePropertyValue(propertyName, rawValue, metadata, source);
    if (!nextValue.valid) {
      if (source === 'plugin' && rawValue !== undefined) {
        console.warn(`Ignoring invalid value emitted by plugin editor for "${propertyName}".`, rawValue);
      }
      return;
    }

    this._target[propertyName] = nextValue.value;
    this.dispatchEvent(new CustomEvent('change', {
      detail: {
        property: propertyName,
        value: nextValue.value,
        target: this._target
      },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Coerces incoming values using the field metadata type hint.
   * @param {string} propertyName - Property name
   * @param {unknown} rawValue - Incoming value
   * @param {QdsConfigField} metadata - Field metadata
   * @param {'native' | 'plugin'} source - Value source
   * @returns {{ valid: boolean; value?: unknown }}
   * @private
   */
  private _coercePropertyValue(
    propertyName: string,
    rawValue: unknown,
    metadata: QdsConfigField,
    source: 'native' | 'plugin'
  ): { valid: boolean; value?: unknown } {
    const isStructuredPluginValue = (
      source === 'plugin' &&
      Boolean(metadata.editorTag) &&
      metadata.type !== 'typography' &&
      !(rawValue instanceof Date) &&
      (Array.isArray(rawValue) || (!!rawValue && typeof rawValue === 'object'))
    );

    if (isStructuredPluginValue) {
      return { valid: true, value: cloneEditorValue(rawValue) };
    }

    switch (metadata.type) {
      case 'number': {
        const numericValue = typeof rawValue === 'number' ? rawValue : Number.parseFloat(String(rawValue ?? ''));
        if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
          return { valid: true, value: numericValue };
        }
        return source === 'native'
          ? { valid: true, value: 0 }
          : { valid: false };
      }

      case 'boolean':
        if (typeof rawValue === 'boolean') {
          return { valid: true, value: rawValue };
        }
        if (typeof rawValue === 'string') {
          return { valid: true, value: rawValue === 'true' };
        }
        return { valid: false };

      case 'date': {
        if (rawValue instanceof Date && !Number.isNaN(rawValue.getTime())) {
          return { valid: true, value: rawValue };
        }

        const dateValue = new Date(String(rawValue ?? ''));
        if (!Number.isNaN(dateValue.getTime())) {
          return { valid: true, value: dateValue };
        }

        return source === 'native'
          ? { valid: true, value: new Date() }
          : { valid: false };
      }

      case 'string':
      case 'textarea':
      case 'color':
      case 'select':
        if (typeof rawValue === 'string') {
          return { valid: true, value: rawValue };
        }
        if ((rawValue === null || rawValue === undefined) && source === 'native') {
          return { valid: true, value: '' };
        }
        return { valid: false };

      case 'typography': {
        if (rawValue === null || rawValue === undefined) {
          return { valid: true, value: undefined };
        }

        const typographyValue = normalizeTypographyStyle(rawValue);
        if (typographyValue) {
          return { valid: true, value: typographyValue };
        }

        return source === 'plugin'
          ? { valid: true, value: undefined }
          : { valid: false };
      }

      default:
        if (typeof rawValue === 'string') {
          return { valid: true, value: rawValue };
        }
        if ((rawValue === null || rawValue === undefined) && source === 'native') {
          return { valid: true, value: '' };
        }
        console.warn(`Unsupported property editor type "${metadata.type}" for "${propertyName}".`);
        return { valid: false };
    }
  }
  
  /**
   * Gets or creates metadata for a specific property
   * If metadata is missing, default metadata is created based on the JavaScript type
   * @param {string} propertyName - The name of the property
   * @returns {Object} The metadata object for the property
   * @private
   */
  private _getMetadataForProperty(propertyName: string): QdsConfigField {
    // If we have explicit metadata for this property, use it
    if (this._metadata[propertyName]) {
      return this._metadata[propertyName];
    }
    
    // Otherwise, create default metadata based on the JavaScript type
    const value = this._target[propertyName];
    const valueType = value === null || value === undefined ? 'string' : typeof value;
    
    const defaultMetadata: QdsConfigField = {
      displayName: this._formatDisplayName(propertyName),
      description: '',
      type: 'string',
    };
    
    // Add additional metadata based on type
    switch (valueType) {
      case 'number':
        defaultMetadata.type = 'number';
        defaultMetadata.min = Number.MIN_SAFE_INTEGER;
        defaultMetadata.max = Number.MAX_SAFE_INTEGER;
        defaultMetadata.step = 1;
        break;
      case 'boolean':
        defaultMetadata.type = 'boolean';
        // No additional properties needed
        break;
      case 'object':
        if (value instanceof Date) {
          defaultMetadata.type = 'date';
        }
        break;
      default:
        defaultMetadata.type = 'string';
        break;
    }
    
    return defaultMetadata;
  }
  
  /**
   * Formats a property name for display
   * Converts camelCase or snake_case to Title Case with spaces
   * @param {string} name - The property name
   * @returns {string} The formatted display name
   * @private
   */
  private _formatDisplayName(name: string): string {
    return name
      // Add spaces before capital letters and uppercase the first character
      .replace(/([A-Z])/g, ' $1')
      // Replace underscores with spaces
      .replace(/_/g, ' ')
      // Trim leading spaces and capitalize the first letter
      .replace(/^./, str => str.toUpperCase())
      // Capitalize the first letter after spaces
      .replace(/ ./g, str => str.toUpperCase());
  }
  
  /**
   * Creates the HTML for a property editor based on its type
   * @param {string} key - The property key
   * @param {any} value - The property value
   * @param {Object} metadata - The metadata for the property
   * @returns {string} The HTML for the property editor
   * @private
   */
  private _createEditorForProperty(key: string, value: any, metadata: QdsConfigField): string {
    const type = metadata.type;
    const id = `prop-${key}`;
    const disabledAttribute = this._readonly ? 'disabled' : '';
    const maybeWrapWithFontPicker = (editorMarkup: string): string => {
      if (!metadata.typographyKey || !customElements.get('qds-font-picker')) {
        return editorMarkup;
      }

      return `
        <div class="property-editor-inline">
          <div class="property-editor-inline__control">
            ${editorMarkup}
          </div>
          <qds-font-picker
            id="${escapeHtml(id)}-font"
            data-property="${escapeHtml(metadata.typographyKey)}"
            data-plugin-editor="true"
            aria-label="${escapeHtml(metadata.displayName)} font styling"
            class="property-plugin property-plugin--inline"
          ></qds-font-picker>
        </div>
      `;
    };
    const createPluginMarkup = (tagName: string): string => {
      // Only emit a custom-element tag when the name is well-formed
      // (letters/digits/`-` and contains a hyphen). Guards against injecting
      // arbitrary markup if a malformed tag ever reaches this path.
      if (!/^[a-z][a-z0-9]*(-[a-z0-9]+)+$/.test(tagName)) {
        return '';
      }
      return `
        <${tagName}
          id="${escapeHtml(id)}"
          data-property="${escapeHtml(key)}"
          data-plugin-editor="true"
          aria-label="${escapeHtml(metadata.displayName)}"
          class="property-plugin"
        ></${tagName}>
      `;
    };

    if (metadata.editorTag) {
      if (customElements.get(metadata.editorTag)) {
        return createPluginMarkup(metadata.editorTag);
      }

      this._warnAboutMissingPluginEditor(metadata.editorTag, key);
    }
    
    switch (type) {
      case 'boolean':
        return `
          <input 
            type="checkbox" 
            id="${id}" 
            data-property="${key}"
            ${value ? 'checked' : ''}
            aria-label="${metadata.displayName}"
            class="property-input"
            ${disabledAttribute}
          />
        `;
      
      case 'number':
        return `
          <input 
            type="number" 
            id="${id}" 
            data-property="${key}" 
            value="${value}"
            min="${metadata.min !== undefined ? metadata.min : ''}"
            max="${metadata.max !== undefined ? metadata.max : ''}"
            step="${metadata.step !== undefined ? metadata.step : '1'}"
            aria-label="${metadata.displayName}"
            class="property-input"
            ${disabledAttribute}
          />
        `;
      
      case 'date': {
        const dateValue = value instanceof Date 
          ? value.toISOString().split('T')[0] 
          : '';
        return `
          <input 
            type="date" 
            id="${id}" 
            data-property="${key}" 
            value="${dateValue}"
            aria-label="${metadata.displayName}"
            class="property-input"
            ${disabledAttribute}
          />
        `;
      }
      
      case 'color':
        if (customElements.get('qds-color-picker')) {
          return createPluginMarkup('qds-color-picker');
        }

        return `
          <input 
            type="color" 
            id="${id}" 
            data-property="${key}" 
            value="${this._normalizeColorValue(value) || this._getDefaultColorValue()}"
            aria-label="${metadata.displayName}"
            class="property-input color-input"
            ${disabledAttribute}
          />
        `;

      case 'typography':
        if (customElements.get('qds-font-picker')) {
          return createPluginMarkup('qds-font-picker');
        }

        return `
          <input 
            type="text" 
            id="${id}" 
            data-property="${key}" 
            value="${JSON.stringify(value || {})}"
            aria-label="${metadata.displayName}"
            class="property-input"
            ${disabledAttribute}
          />
        `;
      
      case 'textarea':
        return maybeWrapWithFontPicker(`
          <textarea
            id="${id}"
            data-property="${key}"
            aria-label="${escapeHtml(metadata.displayName)}"
            class="property-input property-input--textarea"
            ${metadata.placeholder ? `placeholder="${escapeHtml(metadata.placeholder)}"` : ''}
            ${disabledAttribute}
          >${value !== null && value !== undefined ? escapeHtml(value) : ''}</textarea>
        `);

      case 'select':
        if (metadata.options && Array.isArray(metadata.options)) {
          const options = metadata.options.map((option) => {
            const optionValue = typeof option === 'object' ? option.value : option;
            const optionLabel = typeof option === 'object' ? option.label : option;
            return `
              <option
                value="${escapeHtml(optionValue)}"
                ${optionValue === value ? 'selected' : ''}
              >
                ${escapeHtml(optionLabel)}
              </option>
            `;
          }).join('');

          return `
            <select
              id="${id}"
              data-property="${key}"
              aria-label="${escapeHtml(metadata.displayName)}"
              class="property-input"
              ${disabledAttribute}
            >
              ${options}
            </select>
          `;
        }
        // Options aren't valid — fall through to the default text input.

      default: // string or any other type
        return maybeWrapWithFontPicker(`
          <input
            type="text"
            id="${id}"
            data-property="${key}"
            value="${value !== null && value !== undefined ? escapeHtml(value) : ''}"
            aria-label="${escapeHtml(metadata.displayName)}"
            class="property-input"
            ${metadata.placeholder ? `placeholder="${escapeHtml(metadata.placeholder)}"` : ''}
            ${metadata.pattern ? `pattern="${escapeHtml(metadata.pattern)}"` : ''}
            ${disabledAttribute}
          />
        `);
    }
  }
  
  /**
   * Renders all property editors
   * @returns {string} The HTML for all property editors
   * @private
   */
  private _renderPropertyEditors() {
    if (!this._target || Object.keys(this._target).length === 0) {
      return '<div class="no-properties">No properties available</div>';
    }

    const propertyKeys = Array.from(new Set([
      ...Object.keys(this._target),
      ...Object.keys(this._metadata || {})
    ]));

    return propertyKeys
      .filter((key) => !this._getMetadataForProperty(key).hidden)
      .map(key => {
      const value = this._target[key];
      const metadata = this._getMetadataForProperty(key);
      const editor = this._createEditorForProperty(key, value, metadata);
      
      return `
        <div class="property-row" title="${metadata.description || ''}">
          <div class="property-label">
            <label for="prop-${key}">${metadata.displayName}</label>
          </div>
          <div class="property-editor">
            ${editor}
          </div>
        </div>
      `;
    }).join('');
  }

  /**
   * Binds host context and current values into rendered plugin editors.
   * @private
   */
  private _hydratePluginEditors(): void {
    if (!this.shadowRoot) {
      return;
    }

    const pluginEditors = this.shadowRoot.querySelectorAll<HTMLElement>('[data-plugin-editor="true"]');
    pluginEditors.forEach((element) => {
      const propertyName = element.dataset.property;
      if (!propertyName) {
        return;
      }

      const pluginEditor = element as QdsPropertyFieldPluginElement;
      pluginEditor.property = propertyName;
      pluginEditor.value = this._target[propertyName];
      pluginEditor.metadata = this._getMetadataForProperty(propertyName);
      pluginEditor.context = this._context;
      pluginEditor.target = this._target;
      pluginEditor.readonly = this._readonly;
      if (this._readonly) {
        pluginEditor.setAttribute('readonly', '');
      } else {
        pluginEditor.removeAttribute('readonly');
      }
    });
  }

  /**
   * Warns once per missing editor tag and falls back to the built-in editor.
   * @param {string} editorTag - Unregistered custom element tag
   * @param {string} propertyName - Field property name
   * @private
   */
  private _warnAboutMissingPluginEditor(editorTag: string, propertyName: string): void {
    if (this._warnedMissingEditors.has(editorTag)) {
      return;
    }

    this._warnedMissingEditors.add(editorTag);
    console.warn(`Plugin editor "${editorTag}" is not registered for "${propertyName}". Falling back to the built-in control.`);
  }

  /**
   * Normalizes incoming colors to a 6-digit hex value for input[type="color"]
   * @param {unknown} value - Color candidate
   * @returns {string | null}
   * @private
   */
  private _normalizeColorValue(value: unknown): string | null {
    if (typeof value !== 'string') return null;

    const trimmed = value.trim();
    const longHexMatch = trimmed.match(/^#([0-9a-fA-F]{6})$/);
    if (longHexMatch) {
      return `#${longHexMatch[1].toUpperCase()}`;
    }

    const shortHexMatch = trimmed.match(/^#([0-9a-fA-F]{3})$/);
    if (shortHexMatch) {
      const expanded = shortHexMatch[1]
        .split('')
        .map((char) => `${char}${char}`)
        .join('');

      return `#${expanded.toUpperCase()}`;
    }

    const rgbMatch = trimmed.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
    if (rgbMatch) {
      const hexChannels = rgbMatch.slice(1, 4).map((channel) => {
        const clamped = Math.max(0, Math.min(255, Number.parseInt(channel, 10)));
        return clamped.toString(16).padStart(2, '0');
      });

      return `#${hexChannels.join('').toUpperCase()}`;
    }

    return null;
  }

  /**
   * Resolves a CSS color expression into a normalized hex value.
   * @param {string} value - The CSS color expression to resolve
   * @returns {string | null} The normalized hex color
   * @private
   */
  private _resolveColorValue(value: string): string | null {
    if (!value) return null;

    const probe = document.createElement('span');
    probe.style.color = value;
    probe.style.display = 'none';
    document.body.appendChild(probe);

    const resolvedColor = getComputedStyle(probe).color;
    probe.remove();

    return this._normalizeColorValue(resolvedColor);
  }

  /**
   * Uses the current theme accent as the default color token fallback.
   * @returns {string}
   * @private
   */
  private _getDefaultColorValue(): string {
    const rootStyles = getComputedStyle(document.documentElement);
    const accentColor = rootStyles.getPropertyValue('--theme-accent-primary').trim();
    const brandAccentColor = rootStyles.getPropertyValue('--brand-middle-blue').trim();
    const fallbackTextColor = getComputedStyle(document.body || document.documentElement).color.trim();

    return this._resolveColorValue(accentColor)
      || this._resolveColorValue(brandAccentColor)
      || this._resolveColorValue(fallbackTextColor)
      || this._resolveColorValue(rootStyles.color.trim())!;
  }
  
  /**
   * Renders the component
   */
  render() {
    if (!this.shadowRoot) return;
    
    const propertyEditors = this._renderPropertyEditors();
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          font-family: var(--qds-font-family, var(--theme-font-family, 'IBM Plex Sans', sans-serif));
          color: var(--qds-text-color, var(--theme-text-primary));
          background-color: var(--qds-background-color, var(--theme-background-primary));
          border: 1px solid var(--qds-border-color, var(--theme-border-primary));
          border-radius: var(--qds-property-editor-radius, var(--theme-radius-sm, 4px));
          box-shadow: var(--qds-property-editor-shadow, var(--theme-shadow-xs));
          margin: var(--qds-property-editor-margin-block, var(--theme-space-2, 8px)) 0;
          --property-editor-title-font-size: var(--qds-property-editor-title-font-size, var(--theme-font-size-sm, 14px));
          --property-editor-label-font-size: var(--qds-property-editor-label-font-size, var(--theme-font-size-sm, 14px));
          --property-editor-input-font-size: var(--qds-property-editor-input-font-size, var(--theme-font-size-sm, 14px));
          --property-editor-title-font-weight: var(--qds-property-editor-title-font-weight, var(--theme-font-weight-semibold, 600));
          --property-editor-header-padding-block: var(--qds-property-editor-header-padding-block, var(--theme-space-2, 8px));
          --property-editor-header-padding-inline: var(--qds-property-editor-header-padding-inline, var(--theme-space-3, 12px));
          --property-editor-container-padding: var(--qds-property-editor-container-padding, var(--theme-space-3, 12px));
          --property-editor-row-gap: var(--qds-property-editor-row-gap, var(--theme-space-3, 12px));
          --property-editor-label-gap: var(--qds-property-editor-label-gap, var(--theme-space-3, 12px));
          --property-editor-input-padding-block: var(--qds-property-editor-input-padding-block, var(--theme-space-2, 8px));
          --property-editor-input-padding-inline: var(--qds-property-editor-input-padding-inline, var(--theme-space-2, 8px));
          --property-editor-expander-size: var(--qds-property-editor-expander-size, var(--icon-size, 20px));
          --property-editor-expander-radius: var(--qds-property-editor-expander-radius, var(--theme-radius-sm, 4px));
          --property-editor-expander-background: var(--qds-property-editor-expander-background, var(--theme-background-hover));
          --property-editor-expander-hover-background: var(--qds-property-editor-expander-hover-background, var(--theme-background-tertiary));
          --property-editor-input-height: var(--qds-property-editor-input-height, calc(var(--theme-space-6, 24px) + var(--theme-space-2, 8px)));
        }
        
        *, *::before, *::after {
          box-sizing: border-box;
        }
        
        .property-editor-header {
          display: flex;
          align-items: center;
          padding: var(--property-editor-header-padding-block) var(--property-editor-header-padding-inline);
          background-color: var(--qds-header-background, var(--theme-background-secondary));
          border-bottom: 1px solid var(--qds-border-color, var(--theme-border-primary));
          cursor: pointer;
          user-select: none;
          border-top-left-radius: var(--qds-property-editor-radius, var(--theme-radius-sm, 4px));
          border-top-right-radius: var(--qds-property-editor-radius, var(--theme-radius-sm, 4px));
          transition:
            background-color var(--transition-speed-fast, 0.15s) ease,
            border-color var(--transition-speed-fast, 0.15s) ease;
        }
        
        .property-editor-header:hover {
          background-color: var(--qds-header-hover-background, var(--theme-background-hover));
        }

        .property-editor-header:focus-visible {
          outline: 2px solid var(--qds-focus-color, var(--theme-accent-primary));
          outline-offset: 2px;
        }
        
        .property-editor-title {
          flex: 1;
          font-weight: var(--property-editor-title-font-weight);
          font-size: var(--property-editor-title-font-size);
        }
        
        .property-editor-expander {
          width: var(--property-editor-expander-size);
          height: var(--property-editor-expander-size);
          display: flex;
          align-items: center;
          justify-content: center;
          transition:
            transform var(--transition-speed-fast, 0.15s) ease,
            background-color var(--transition-speed-fast, 0.15s) ease;
          background-color: var(--property-editor-expander-background);
          border-radius: var(--property-editor-expander-radius);
          margin-left: var(--theme-space-1, 4px);
          cursor: pointer;
        }
        
        .property-editor-expander:hover {
          background-color: var(--property-editor-expander-hover-background);
        }
        
        .property-editor-expander::before {
          content: '';
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid var(--qds-text-color, var(--theme-text-primary));
          transform: ${this._expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
          transition: transform var(--transition-speed-fast, 0.15s) ease;
        }
        
        .property-container {
          display: ${this._expanded ? 'block' : 'none'};
          padding: var(--property-editor-container-padding);
        }
        
        .property-row {
          display: flex;
          margin-bottom: var(--property-editor-row-gap);
          align-items: center;
          width: 100%;
          box-sizing: border-box;
        }
        
        .property-label {
          flex: 0 0 40%;
          font-size: var(--property-editor-label-font-size);
          padding-right: var(--property-editor-label-gap);
          box-sizing: border-box;
        }
        
        .property-editor {
          flex: 0 0 60%;
          box-sizing: border-box;
        }

        .property-editor-inline {
          display: flex;
          align-items: flex-start;
          gap: var(--theme-space-2, 8px);
          width: 100%;
          min-width: 0;
        }

        .property-editor-inline__control {
          flex: 1 1 auto;
          min-width: 0;
        }
        
        .no-properties {
          padding: var(--theme-space-2, 8px);
          font-style: italic;
          font-size: var(--property-editor-label-font-size);
          color: var(--qds-text-muted-color, var(--theme-text-tertiary));
        }
        
        .property-input {
          width: 100%;
          min-height: var(--property-editor-input-height);
          padding: var(--property-editor-input-padding-block) var(--property-editor-input-padding-inline);
          border: 1px solid var(--qds-input-border-color, var(--theme-border-primary));
          border-radius: var(--qds-property-editor-radius, var(--theme-radius-sm, 4px));
          font-size: var(--property-editor-input-font-size);
          background-color: var(--qds-input-background, var(--theme-background-primary));
          color: var(--qds-input-text-color, var(--theme-text-primary));
          box-sizing: border-box;
          transition:
            border-color var(--transition-speed-fast, 0.15s) ease,
            box-shadow var(--transition-speed-fast, 0.15s) ease,
            background-color var(--transition-speed-fast, 0.15s) ease;
        }
        
        .property-input:focus {
          outline: none;
          border-color: var(--qds-focus-color, var(--theme-accent-primary));
          box-shadow: 0 0 0 2px var(--button-focus-ring);
        }

        .property-input:disabled {
          cursor: not-allowed;
          opacity: 0.7;
          background-color: var(--qds-input-disabled-background, var(--theme-background-secondary));
        }

        input[type="checkbox"] {
          width: calc(var(--icon-size, 20px) - var(--theme-space-1, 4px));
          height: calc(var(--icon-size, 20px) - var(--theme-space-1, 4px));
          margin: 0;
        }
        
        .color-input {
          padding: 2px;
          height: var(--property-editor-input-height);
        }
        
        select.property-input {
          height: var(--property-editor-input-height);
        }

        .property-input--textarea {
          min-height: calc(var(--property-editor-input-height) * 2.5);
          resize: vertical;
          font: inherit;
          line-height: 1.5;
        }

        .property-plugin {
          display: block;
          width: 100%;
        }

        .property-plugin--inline {
          flex: 0 0 auto;
          width: auto;
        }
        
        @media (max-width: 600px) {
          .property-row {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .property-label,
          .property-editor {
            flex: 0 0 100%;
            width: 100%;
            margin-bottom: 4px;
          }
          
          .property-label {
            margin-bottom: 4px;
          }

          .property-editor-inline {
            width: 100%;
          }
        }
      </style>
      
      <div class="property-editor" part="editor">
        <div class="property-editor-header" part="header" id="editor-header" 
             aria-expanded="${this._expanded}" role="button" tabindex="0">
          <div class="property-editor-title" part="title">${this._label}</div>
          <div class="property-editor-expander" part="expander" id="expander-button"></div>
        </div>
        <div class="property-container" part="container" aria-hidden="${!this._expanded}">
          ${propertyEditors}
        </div>
      </div>
    `;

    this._hydratePluginEditors();
  }
}

// Define the custom element
if (!customElements.get('qds-property-editor')) {
  customElements.define('qds-property-editor', PropertyEditor);
}

// Export default for direct import
export default PropertyEditor;
